<template>
  <form v-on:submit.prevent class="SubForm">
    <!-- Quote + Project Info -->
    <div class="row">
      <div class="column">
        <h3>Generate a Quote</h3>
        <label for="projSearch">Project</label>
        <SearchableDropdown
          v-model="newQuote.project"
          :items="projectsForDropdown"
          item-key="id"
          item-label="displayName"
          placeholder="Search projects..."
          input-id="projSearch"
          @change="handleProjectChange"
        />

        <label for="modSearch">Locker Module</label>
        <SearchableDropdown
          v-model="newQuote.locker_combination"
          :items="lockersForDropdown"
          item-key="id"
          item-label="name"
          placeholder="Search locker modules..."
          input-id="modSearch"
          @change="handleLockerChange"
        />
        <label for="dataPackOption">Add $295 Data Pack</label>
        <input id="dataPackOption" type="checkbox" v-model="newQuote.dataPackOption"
          @change="handleDataPackChange" />
        
        <!-- Document type hidden - always defaults to Quote -->
        <input type="hidden" v-model="newQuote.document_type" value="Quote" />
      </div>
      
      <!-- Support Fees -->
      <div class="column">
        <h3>Fees and Pricing</h3>
        <div class="row" v-if="$store.User.admin">
          <div class="column">
            <label for="feePlat"> Yearly Platinum Fee ($)</label>
            <input id="feePlat" type="number" step="0.01" v-model="newQuote.fee_platinum" />
            <label for="feeSilver">Yearly Silver Fee ($)</label>
            <input id="feeSilver" type="number" step="0.01" v-model="newQuote.fee_silver" />
            <label for="priceOverride">Total Locker Price ($)</label>
            <input id="priceOverride" type="text" v-model="newQuote.priceOverride" />
          </div>
          <div class="column">
            <label for="feeGold"> Yearly Gold Fee ($)</label>
            <input id="feeGold" type="number" step="0.01" v-model="newQuote.fee_gold" />
            <label for="feeService">Service Fee ($)</label>
            <input id="feeService" type="number" step="0.01" v-model="newQuote.service_fee" />
            <label for="weeklyRentalFee">Weekly Rent-to-Own ($)</label>
            <input id="weeklyRentalFee" type="text" v-model="newQuote.weekly_rental_fee_override" />
          </div>
        </div>
        <div class="row" v-else>
          <p>Only MPL CRM Admins can override pricing. Contact Kim or Ian if you need to offer quote discounts</p>
        </div>
      </div>
    </div>

    <!-- Adidtional Quote Products -->
    <h3>Additional Products&hellip;</h3>
    <button type="button" @click="addProduct">Add Product</button> or <button type="button" @click="delProduct">Remove Last Product</button>
    <div class="SubForm" v-for="(prod, index) in newQuote.products" :key="index">
      <div class="row">
        <div class="column">
          <label>Product:</label>
          <select v-model="newQuote.products[index].id">
            <option v-for="prodOpt in availProducts" :key="prodOpt.id" :value="prodOpt.id">{{prodOpt.name}} - ${{prodOpt.price.toFixed(2)}}</option>
          </select>
        </div>  
        <div class="column">
          <label>Quantity:</label>
          <input type="number" v-model="newQuote.products[index].qty" />
        </div>
      </div>
    </div>

    <!-- Quote Terms & Conditions -->
    <h3>Notes</h3>
    <textarea v-model="newQuote.notes"></textarea>
    <button type="button" @click="generateQuote">Generate Quote</button>
  </form>
</template>

<script>
import { CallApi } from "../Utilities";
import SearchableDropdown from "./SearchableDropdown.vue";

function productTemplate() {
  return { id: 0, qty: 0 };
}

function EmptyNewQuote() {
  return {
    project: null,
    locker_combination: null,
    products: [],
    service_fee: 0,
    fee_platinum: 0,
    fee_gold: 0,
    fee_silver: 0,
    priceOverride: "AUTO",
    weekly_rental_fee_override: "AUTO",
    is_outdoor: 0,
    notes: "",
    dataPackOption: 0,
    document_type: "Quote"
  };
}

export default {
  name: "QuoteCreate",
  components: { SearchableDropdown },
  props: {
    availProjects: { type: Array, default: () => [] },
    availLockerCombs: { type: Array, default: () => [] },
    availProducts: { type: Array, default: () => [] },
    serviceFees: { type: Array, default: () => [] },
    rtoCoefficients: { type: Object, default: null }
  },
  data() {
    return {
      newQuote: EmptyNewQuote()
    };
  },
  computed: {
    projectsForDropdown() {
      return this.availProjects.map(p => ({
        id: p.id,
        displayName: `${p.project} | ${p.customer}`,
        project: p.project,
        customer: p.customer
      }));
    },
    lockersForDropdown() {
      return this.availLockerCombs;
    }
  },
  methods: {
    fuzzyMatch(searchText, targetText) {
      const search = searchText.toLowerCase().trim();
      const target = targetText.toLowerCase();

      if (!search) return true;

      const searchTerms = search.split(/\s+/).map(term => {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return `(?:^|[^a-z0-9])${escaped}`;
      });

      const pattern = searchTerms.map(term => `(?=.*${term})`).join('');
      const regex = new RegExp(pattern);

      return regex.test(target);
    },
    handleProjectChange() {
      this.updateSupportFees();
    },
    handleDataPackChange() {
      this.updateSupportFees();
    },
    handleLockerChange() {
      this.updateSupportFees();
    },
    updateSupportFees() {
      if (!this.newQuote.project || !this.newQuote.locker_combination) { return; }
      const selectedProject = this.availProjects.filter( a => a.id == this.newQuote.project )[0];
      const selectedLocker = this.availLockerCombs.filter( a => a.id == this.newQuote.locker_combination )[0];
      const numApartments = selectedProject.num_apartments;
      const numLockers = selectedLocker.totalLockers;

      const lockerPrice = parseFloat(selectedLocker.totalPrice);
      if (!selectedLocker.totalPrice || isNaN(lockerPrice) || lockerPrice <= 0) {
        alert(`ERROR: The selected locker combination "${selectedLocker.name}" does not have a valid price configured in the system.\n\nPlease contact your administrator to set up pricing for this locker combination before creating a quote.`);
        this.newQuote.locker_combination = null;
        return;
      }

  const yearlyFees = this.serviceFees.filter(a => {
        const satisfiesApartments = numApartments >= a.min_apartments && numApartments <= a.max_apartments;
        const satisfiesLockers = numLockers >= a.min_doors && numLockers <= a.max_doors;
        return satisfiesApartments && satisfiesLockers;
      });

      if (!yearlyFees[0]) {
        alert(`ERROR: No service fee configuration found for this combination.\n\nProject: ${numApartments} apartments\nLocker: ${numLockers} doors\n\nPlease contact your administrator to configure service fees for this range.`);
        this.newQuote.locker_combination = null;
        return;
      }

      this.newQuote.fee_silver = yearlyFees[0].fee_silver;
      this.newQuote.fee_gold = yearlyFees[0].fee_gold;
      this.newQuote.fee_platinum = yearlyFees[0].fee_platinum;
      this.newQuote.service_fee = yearlyFees[0].service_fee;

      this.newQuote.priceOverride = `AUTO: $${lockerPrice.toFixed(2)}`;

      if (this.rtoCoefficients) {
        const c = this.rtoCoefficients;
  const serviceFee = parseFloat(yearlyFees[0].service_fee) || 0;
  const platinumFee = parseFloat(yearlyFees[0].fee_platinum) || 0;

        const weeklyFee = (
          (c.rto_pct_locker_price / 100) * (lockerPrice  + (this.newQuote.dataPackOption ? 295.0 : 0.0)) +
          (c.rto_pct_service_fee / 100) * serviceFee +
          (c.rto_pct_platinum_fee / 100) * platinumFee +
          c.rto_mult_num_doors * numLockers +
          c.rto_mult_num_apartments * numApartments +
          c.rto_additional_cost
        ) / c.rto_periods_per_year;

        this.newQuote.weekly_rental_fee_override = `AUTO: $${weeklyFee.toFixed(2)}`;
      } else {
        this.newQuote.weekly_rental_fee_override = "AUTO";
      }
    },
    addProduct() { this.newQuote.products.push(productTemplate()); },
    delProduct() { this.newQuote.products.pop(); },
    async generateQuote() {
      if (!this.newQuote.project) { alert("Please select a Project"); return; }
      if (!this.newQuote.locker_combination) { alert("Please select a Locker Module"); return; }

      if (this.$store.User.admin) {
        const getPriceValue = (val) => {
          if (typeof val === 'string' && val.startsWith('AUTO:')) {
            return parseFloat(val.replace(/^AUTO:\s*\$?/, ''));
          }
          return parseFloat(val);
        };

        const feePlatinum = parseFloat(this.newQuote.fee_platinum);
        const feeGold = parseFloat(this.newQuote.fee_gold);
        const feeSilver = parseFloat(this.newQuote.fee_silver);
        const serviceFee = parseFloat(this.newQuote.service_fee);
        const priceOverride = getPriceValue(this.newQuote.priceOverride);
        const weeklyRentalFee = getPriceValue(this.newQuote.weekly_rental_fee_override);

        const validationErrors = [];
        if (isNaN(feePlatinum) || feePlatinum < 0) validationErrors.push("- Platinum Fee is invalid or missing");
        if (isNaN(feeGold) || feeGold < 0) validationErrors.push("- Gold Fee is invalid or missing");
        if (isNaN(feeSilver) || feeSilver < 0) validationErrors.push("- Silver Fee is invalid or missing");
        if (isNaN(serviceFee) || serviceFee < 0) validationErrors.push("- Service Fee is invalid or missing");
        if (isNaN(priceOverride) || priceOverride <= 0) validationErrors.push("- Total Locker Price is invalid or missing");
        if (isNaN(weeklyRentalFee) || weeklyRentalFee <= 0) validationErrors.push("- Weekly Rent-to-Own is invalid or missing");

        if (validationErrors.length > 0) {
          alert("Cannot generate quote. The following pricing fields have errors:\n\n" + validationErrors.join("\n") + "\n\nPlease select a Project and Locker to auto-populate these fields, or enter valid values manually.");
          return;
        }
      }

      const apiResult = await CallApi("POST", "/quotes/new", this.newQuote);
      if (!apiResult.success) {
        const errorMessage = apiResult.messages && apiResult.messages.length > 0
          ? apiResult.messages.join('\n')
          : "New Quote Couldn't Be Created";
        alert(errorMessage);
        return;
      }

      // Notify parent to refresh and close form
      this.$emit('created');
      // Reset form
      this.newQuote = EmptyNewQuote();
    }
  }
};
</script>

<style scoped>
/* Keep any component-scoped styles (none required beyond parent) */
</style>
